import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Utensils, 
  LayoutDashboard, 
  ClipboardList, 
  Calendar, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  TrendingUp,
  DollarSign,
  ShoppingBag,
  Users,
  Clock,
  Sparkles,
  ChevronRight,
  ExternalLink,
  BookOpen,
  Printer,
  Radio,
  Crown,
  QrCode,
  History,
  Tag,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LegalGate } from '../components/LegalGate';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI, authAPI, orderAPI, reservationAPI, seedAPI } from '../lib/api';
import { formatPrice, formatDateTime, getStatusColor, getOrderTypeLabel } from '../lib/utils';
import { toast } from 'sonner';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Radio, label: 'Live Bestellingen', path: '/dashboard/live-orders', highlight: true },
  { icon: History, label: 'Alle Bestellingen', path: '/dashboard/orders-history' },
  { icon: Users, label: 'Klanten', path: '/dashboard/customers' },
  { icon: BookOpen, label: 'Menu', path: '/dashboard/menu' },
  { icon: Tag, label: 'Add-ons', path: '/dashboard/modifiers' },
  { icon: Calendar, label: 'Reserveringen', path: '/dashboard/reservations' },
  { icon: TrendingUp, label: 'Bezorgzones', path: '/dashboard/delivery-zones' },
  { icon: Printer, label: 'Printer', path: '/dashboard/printer' },
  { icon: QrCode, label: 'Marketing', path: '/dashboard/marketing' },
  { icon: Crown, label: 'Abonnement', path: '/dashboard/subscription' },
  { icon: Sparkles, label: 'AI Inzichten', path: '/dashboard/ai-insights' },
  { icon: Settings, label: 'Instellingen', path: '/dashboard/settings' },
];

export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { restaurant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    toast.success('Succesvol uitgelogd');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Blokkeert het dashboard zolang een nieuwe versie van de voorwaarden
          nog niet bevestigd is. */}
      <LegalGate onLogout={handleLogout} />

      {/* Demo-omgeving. De backend weigert hier elke schrijfactie; deze balk
          maakt vooraf duidelijk waarom een opslagpoging niet doorgaat. */}
      {restaurant?.is_demo && (
        <div className="sticky top-0 z-40 bg-amber-400 text-amber-950 px-4 py-2 text-sm font-medium
                        flex items-center justify-center gap-2 text-center"
             data-testid="demo-balk">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span>
            Demo-omgeving — je kunt alles bekijken, maar wijzigingen worden niet opgeslagen.
          </span>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-100 z-50
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
                <Utensils className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-[#1A4D2E]">FoodFlows</span>
            </Link>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium transition-all text-sm
                    ${isActive 
                      ? 'bg-[#1A4D2E] text-white' 
                      : item.highlight 
                        ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                        : 'text-gray-600 hover:bg-[#D8F3DC] hover:text-[#1A4D2E]'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className={`w-5 h-5 flex-shrink-0 ${item.highlight && !isActive ? 'text-emerald-600' : ''}`} />
                  <span className="truncate">{item.label}</span>
                  {item.highlight && !isActive && (
                    <span className="ml-auto w-2 h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0"></span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Restaurant Webshop Link */}
          {restaurant?.slug && (
            <div className="p-4 border-t border-gray-100">
              <Link
                to={`/shop/${restaurant.slug}`}
                target="_blank"
                className="flex items-center justify-between p-3 bg-[#D8F3DC] rounded-xl text-[#1A4D2E] hover:bg-[#c0ebc6] transition-colors"
                data-testid="view-webshop-link"
              >
                <span className="font-medium text-sm">Bekijk je Webshop</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Logout */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Uitloggen</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex-1 lg:flex-none">
              <h1 className="font-heading text-lg font-semibold text-gray-900 truncate">
                {restaurant?.name || 'Dashboard'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-gray-100 rounded-lg relative">
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#E07A5F] rounded-full"></span>
              </button>
              <div className="w-10 h-10 bg-[#D8F3DC] rounded-full flex items-center justify-center">
                <span className="font-medium text-[#1A4D2E]">
                  {restaurant?.name?.charAt(0) || 'R'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export const DashboardOverview = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { restaurant } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First seed demo data if needed
        try {
          await seedAPI.seedDemoData();
        } catch (e) {
          // Ignore if already seeded
        }

        const [analyticsRes, ordersRes] = await Promise.all([
          analyticsAPI.getOverview(),
          orderAPI.getAll(),
        ]);
        setAnalytics(analyticsRes.data);
        setRecentOrders(ordersRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = analytics ? [
    { icon: DollarSign, label: 'Totale Omzet', value: formatPrice(analytics.total_revenue), color: 'bg-green-100 text-green-600' },
    { icon: ShoppingBag, label: 'Totaal Bestellingen', value: analytics.total_orders, color: 'bg-blue-100 text-blue-600' },
    { icon: Clock, label: 'Openstaand', value: analytics.pending_orders, color: 'bg-yellow-100 text-yellow-600' },
    { icon: Users, label: 'Reserveringen', value: analytics.total_reservations, color: 'bg-purple-100 text-purple-600' },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2d6b42] rounded-2xl p-6 text-white">
        <h2 className="font-heading text-2xl font-bold mb-2">
          Welkom terug, {restaurant?.name}!
        </h2>
        <p className="text-white/80">
          Hier is wat er vandaag gebeurt met je restaurant.
        </p>
      </div>

      {/* Nog niet bevestigd e-mailadres: de bestelpagina staat dan offline
          (is_active blijft False tot de link uit de mail geopend is). Zonder
          deze melding zou een nieuwe klant niet weten waarom zijn webshop
          onbereikbaar is. */}
      {restaurant?.email_verified === false && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5"
             data-testid="email-onbevestigd">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Bevestig je e-mailadres</p>
              <p className="text-amber-800 text-sm mt-1">
                Je bestelpagina staat nog niet online. We stuurden een bevestigingsmail
                naar <strong>{restaurant?.email}</strong>. Open de link daarin om je
                account volledig te activeren.
              </p>
              <button
                onClick={async () => {
                  try {
                    const { data } = await authAPI.resendVerification(restaurant.email);
                    toast.success(data.message || 'Nieuwe bevestigingsmail verstuurd.');
                  } catch (e) {
                    toast.error('Versturen mislukt. Probeer het later opnieuw.');
                  }
                }}
                data-testid="verificatie-opnieuw"
                className="mt-3 text-sm font-medium text-amber-900 underline hover:no-underline"
              >
                Stuur de mail opnieuw
              </button>
            </div>
          </div>
        </div>
      )}

      <OnboardingChecklist />

      {/* Webshop Quick Access */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-1">Jouw Webshop</h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="font-mono bg-white px-3 py-1 rounded-lg border border-gray-200">
                {window.location.origin}/shop/{restaurant?.slug}
              </span>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/shop/${restaurant?.slug}`);
                  toast.success('Link gekopieerd!');
                }}
                className="p-1.5 hover:bg-white rounded-lg transition-colors text-emerald-600"
                title="Kopieer link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/shop/${restaurant?.slug}`}
              target="_blank"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Bekijk Webshop
            </Link>
            <Link
              to="/dashboard/settings"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
            >
              <Settings className="w-4 h-4" />
              Beheer
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        {stats.map((stat, index) => (
          <div key={index} className="card-dashboard" data-testid={`stat-card-${index}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
            <p className="font-heading text-2xl font-bold text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="card-dashboard">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-lg font-semibold text-gray-900">Recente Bestellingen</h3>
          <Link to="/dashboard/orders" className="text-[#1A4D2E] hover:underline text-sm font-medium flex items-center gap-1">
            Bekijk alles <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        {recentOrders.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Nog geen bestellingen. Deel je webshop link om bestellingen te ontvangen!</p>
        ) : (
          <div className="space-y-4">
            {recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-testid={`order-row-${order.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <span className="text-lg">{order.order_type === 'delivery' ? '🚗' : order.order_type === 'pickup' ? '🛍️' : '🍽️'}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{order.order_number}</p>
                    <p className="text-sm text-gray-500">{order.customer_name} • {getOrderTypeLabel(order.order_type)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatPrice(order.total)}</p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Link to="/dashboard/menu" className="card-dashboard group hover:border-[#1A4D2E]" data-testid="quick-action-menu">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D8F3DC] rounded-xl flex items-center justify-center group-hover:bg-[#1A4D2E] transition-colors">
              <BookOpen className="w-6 h-6 text-[#1A4D2E] group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 className="font-heading font-semibold text-gray-900">Manage Menu</h4>
              <p className="text-sm text-gray-500">Add items, update prices, and more</p>
            </div>
          </div>
        </Link>
        
        <Link to="/dashboard/ai-insights" className="card-dashboard group hover:border-[#E07A5F]" data-testid="quick-action-ai">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#E07A5F]/10 rounded-xl flex items-center justify-center group-hover:bg-[#E07A5F] transition-colors">
              <Sparkles className="w-6 h-6 text-[#E07A5F] group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 className="font-heading font-semibold text-gray-900">AI Insights</h4>
              <p className="text-sm text-gray-500">Get AI-powered recommendations</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};
